// src/components/purchases/PurchaseInvoiceList.js
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Paper,
  Stack,
  useTheme,
  TablePagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  Checkbox,
} from "@mui/material";
import {
  Edit,
  Delete,
  Search,
  Refresh,
  Payment,
  Visibility,
  CloudUpload,
  CloudDone,
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";
import ConfirmDialog from "../ui/ConfirmDialog";
import {
  PURCHASE_INVOICE_STATUS,
  EMITTER_TYPE_LABELS,
} from "../../utils/models/purchaseInvoiceModel";

const statusColors = {
  unpaid: "warning",
  paid: "success",
};

const statusLabels = {
  unpaid: "Sin pagar",
  paid: "Pagada",
};

const emitterTypeColors = {
  supplier: "info",
  commercial: "secondary",
  client: "primary",
};

// Formatea el número de la factura de compra para mostrarlo en la lista.
// - Si emitterType === 'commercial' y el número es numérico → AUTO/YYYY/XXXX
// - Si emitterType === 'client' y el número es numérico → ABN/YYYY/XXXX
// - Resto: se muestra tal cual (o si ya viene con prefijo AUTO/ABN, se respeta)
const formatPurchaseInvoiceNumberDisplay = (invoice) => {
  const raw = invoice?.invoiceNumber;
  if (!raw || raw === "BORRADOR") return "BORRADOR";

  const emitterType = invoice.emitterType || "supplier";

  const year = invoice.issueDate
    ? new Date(invoice.issueDate).getFullYear()
    : new Date().getFullYear();

  // Comercial → AUTO/YYYY/XXXX (solo si el número es numérico)
  if (emitterType === "commercial") {
    if (String(raw).startsWith("AUTO/")) return raw;

    // Si NO es puramente numérico, mostrar tal cual
    if (!/^\d+$/.test(String(raw).trim())) {
      return raw;
    }

    const number = String(raw).trim().padStart(4, "0");
    return `AUTO/${year}/${number}`;
  }

  // Cliente (abono) → ABN/YYYY/XXXX (solo si el número es numérico)
  if (emitterType === "client") {
    if (String(raw).startsWith("ABN/")) return raw;

    if (!/^\d+$/.test(String(raw).trim())) {
      return raw;
    }

    const number = String(raw).trim().padStart(4, "0");
    return `ABN/${year}/${number}`;
  }

  // Proveedor y resto: tal cual
  return raw;
};

const PurchaseInvoiceList = ({
  onView,
  onEdit,
  compact = false,
  onDriveStatusChange,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: invoices, loading } = useCollection("purchaseInvoices");
  const { data: projects } = useCollection("projects");
  const [localInvoices, setLocalInvoices] = useState(null);
  const { remove, update } = useAuditFirestore("purchaseInvoices");
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [deletingIds, setDeletingIds] = useState(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(7);

  React.useEffect(() => {
    if (invoices) {
      const filteredInvoices = invoices.filter(
        (inv) => !deletingIds.has(inv.id),
      );
      setLocalInvoices(filteredInvoices);
    }
  }, [invoices, deletingIds]);

  React.useEffect(() => {
    setPage(0);
  }, [search, statusFilter]);

  const filtered = useMemo(() => {
    if (!localInvoices) return [];
    const searchLower = search.toLowerCase();

    let result = localInvoices.filter((i) => {
      const projectNames = (i.projects || [])
        .map((p) => projects?.find((proj) => proj.id === p.projectId)?.name || "")
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      const rawNumber = (i.invoiceNumber || "").toLowerCase();
      const formattedNumber =
        formatPurchaseInvoiceNumberDisplay(i).toLowerCase();

      const matchesSearch =
        search === "" ||
        rawNumber.includes(searchLower) ||
        formattedNumber.includes(searchLower) ||
        i.emitterName?.toLowerCase().includes(searchLower) ||
        i.supplierName?.toLowerCase().includes(searchLower) ||
        projectNames.includes(searchLower);

      const matchesStatus = !statusFilter || i.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    result.sort((a, b) => {
      const dateA = a.issueDate ? new Date(a.issueDate) : new Date(0);
      const dateB = b.issueDate ? new Date(b.issueDate) : new Date(0);
      return dateB - dateA;
    });

    return result;
  }, [localInvoices, projects, search, statusFilter]);

  const groupedInvoices = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    filtered.forEach((invoice) => {
      const date = invoice.issueDate ? new Date(invoice.issueDate) : new Date();
      const year = date.getFullYear();
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      const groupKey = `Q${quarter} ${year}`;
      const groupLabel = getQuarterLabel(quarter, year);

      if (!currentGroup || currentGroup.key !== groupKey) {
        currentGroup = {
          key: groupKey,
          label: groupLabel,
          invoices: [],
          totalCount: 0,
          totalAmount: 0,
        };
        groups.push(currentGroup);
      }
      currentGroup.invoices.push(invoice);
      currentGroup.totalCount += 1;
      currentGroup.totalAmount += invoice.total || 0;
    });

    return groups;
  }, [filtered]);

  const totalInvoices = filtered.length;

  const { paginatedGroups } = useMemo(() => {
    let invoiceCount = 0;
    let startGroupIndex = 0;
    let startInvoiceOffset = 0;

    for (let i = 0; i < groupedInvoices.length; i++) {
      const groupSize = groupedInvoices[i].invoices.length;
      if (invoiceCount + groupSize > page * rowsPerPage) {
        startGroupIndex = i;
        startInvoiceOffset = page * rowsPerPage - invoiceCount;
        break;
      }
      invoiceCount += groupSize;
    }

    if (startGroupIndex >= groupedInvoices.length) {
      return { paginatedGroups: [] };
    }

    const result = [];
    let remainingSlots = rowsPerPage;
    let currentGroupIndex = startGroupIndex;
    let currentOffset = startInvoiceOffset;

    while (remainingSlots > 0 && currentGroupIndex < groupedInvoices.length) {
      const group = groupedInvoices[currentGroupIndex];
      const invoicesSlice = group.invoices.slice(
        currentOffset,
        currentOffset + remainingSlots,
      );

      if (invoicesSlice.length > 0) {
        result.push({ ...group, invoices: invoicesSlice });
        remainingSlots -= invoicesSlice.length;
      }

      currentGroupIndex++;
      currentOffset = 0;
    }

    return { paginatedGroups: result };
  }, [groupedInvoices, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTogglePayment = async (invoice, e) => {
    e.stopPropagation();
    try {
      if (invoice.status === PURCHASE_INVOICE_STATUS.PAID) {
        await update(invoice.id, {
          status: PURCHASE_INVOICE_STATUS.UNPAID,
          paidAt: null,
        });
      } else {
        await update(invoice.id, {
          status: PURCHASE_INVOICE_STATUS.PAID,
          paidAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      console.error("Error toggling payment status:", error);
    }
  };

  const handleDriveStatusToggle = async (invoice, e) => {
    e.stopPropagation();
    try {
      const newDriveStatus = !invoice.uploadedToDrive;
      await update(invoice.id, {
        uploadedToDrive: newDriveStatus,
        uploadedToDriveAt: newDriveStatus ? new Date().toISOString() : null,
      });
      if (onDriveStatusChange) {
        onDriveStatusChange(invoice.id, newDriveStatus);
      }
    } catch (error) {
      console.error("Error toggling drive status:", error);
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;

    const invoiceToDelete = deleteTarget;

    setDeletingIds((prev) => new Set([...prev, invoiceToDelete.id]));
    setLocalInvoices((prev) => prev.filter((i) => i.id !== invoiceToDelete.id));
    setDeleteTarget(null);

    try {
      await remove(invoiceToDelete.id);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invoiceToDelete.id);
        return newSet;
      });
    } catch (error) {
      console.error("Error deleting:", error);
      setDeletingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(invoiceToDelete.id);
        return newSet;
      });
      setLocalInvoices((prev) => [...prev, invoiceToDelete]);
    }
  };

  const getEmitterName = (invoice) => {
    return invoice.emitterName || invoice.supplierName || "-";
  };

  const getEmitterType = (invoice) => {
    return invoice.emitterType || "supplier";
  };

  if (loading || !localInvoices) return <LoadingSpinner />;

  return (
    <Box>
      {!compact && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            borderRadius: { xs: "16px", sm: "20px" },
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            gap={1.5}
            flexWrap="wrap"
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              flex={1}
              flexWrap="wrap"
            >
              <TextField
                placeholder="Buscar facturas..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: { xs: "100%", sm: 280, md: 360 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    height: 44,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search
                        fontSize="small"
                        sx={{ color: theme.palette.primary.main }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch("")}>
                        ✖
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {!compact && (
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 160,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      height: 44,
                    },
                  }}
                >
                  <InputLabel>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Estado"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="unpaid">Sin pagar</MenuItem>
                    <MenuItem value="paid">Pagada</MenuItem>
                  </Select>
                </FormControl>
              )}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={`Total: ${totalInvoices}`}
                sx={{
                  height: 44,
                  borderRadius: "12px",
                  fontWeight: 700,
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                  border: "1px solid",
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                }}
              />
              <Tooltip title="Refrescar">
                <IconButton
                  onClick={() => {
                    if (invoices)
                      setLocalInvoices(
                        invoices.filter((inv) => !deletingIds.has(inv.id)),
                      );
                  }}
                  size="small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography
            variant="caption"
            sx={{
              mt: 1,
              display: "block",
              color: theme.palette.text.secondary,
              fontSize: "0.7rem",
              fontStyle: "italic",
            }}
          >
            Puedes buscar por nombre de proyecto, número de factura o emisor
          </Typography>
        </Paper>
      )}

      {filtered?.length === 0 ? (
        <EmptyState
          title={search ? "Sin resultados" : "Sin facturas de compra"}
          description={
            search
              ? "Prueba con otros términos"
              : "Crea tu primera factura de compra"
          }
        />
      ) : (
        <Card
          elevation={2}
          sx={{
            borderRadius: { xs: 1, sm: 2 },
            overflow: "hidden",
            border: "1px solid",
            borderColor: theme.palette.divider,
            background: theme.palette.background.paper,
          }}
        >
          <TableContainer sx={{ overflowX: "auto" }}>
            <Table size={isMobile ? "small" : "medium"}>
              {!compact && (
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(0, 175, 163, 0.1)"
                          : "rgba(0, 95, 101, 0.05)",
                    }}
                  >
                    <TableCell padding="checkbox" sx={{ width: 48 }}>
                      <Tooltip title="Subido al Drive">
                        <CloudDone
                          sx={{
                            fontSize: 18,
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Nº Factura
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Emisor
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Tipo
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Fecha
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Estado
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Total
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontWeight: 600,
                        width: { xs: 130, sm: 170 },
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                      }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {paginatedGroups.map((group) => (
                  <React.Fragment key={group.key}>
                    <TableRow>
                      <TableCell
                        colSpan={compact ? 6 : 8}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "rgba(0, 175, 163, 0.1)"
                              : "rgba(0, 95, 101, 0.05)",
                          py: 1.5,
                          borderBottom: `2px solid ${theme.palette.primary.main}`,
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: theme.palette.primary.main,
                              fontSize: { xs: "0.85rem", sm: "0.95rem" },
                            }}
                          >
                            {group.label}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ ml: "auto" }}
                          >
                            <Chip
                              label={`${group.totalCount} facturas`}
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.primary.main,
                                color: "white",
                                fontWeight: 600,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                            <Chip
                              label={`${group.totalAmount.toLocaleString(
                                "es-ES",
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                },
                              )} €`}
                              size="small"
                              sx={{
                                backgroundColor: theme.palette.success.main,
                                color: "white",
                                fontWeight: 700,
                                fontSize: "0.7rem",
                                height: 22,
                              }}
                            />
                          </Stack>
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {group.invoices.map((invoice, invoiceIndex) => (
                      <TableRow
                        key={invoice.id}
                        hover
                        onClick={() => onView && onView(invoice)}
                        sx={{
                          cursor: onView ? "pointer" : "default",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          backgroundColor:
                            invoiceIndex % 2 === 0
                              ? "transparent"
                              : theme.palette.mode === "dark"
                                ? "rgba(255, 255, 255, 0.02)"
                                : "rgba(0, 0, 0, 0.01)",
                        }}
                      >
                        {!compact && (
                          <TableCell padding="checkbox">
                            <Tooltip
                              title={
                                invoice.uploadedToDrive
                                  ? "Subido al Drive"
                                  : "No subido al Drive"
                              }
                            >
                              <Checkbox
                                checked={invoice.uploadedToDrive || false}
                                onChange={(e) =>
                                  handleDriveStatusToggle(invoice, e)
                                }
                                onClick={(e) => e.stopPropagation()}
                                icon={
                                  <CloudUpload
                                    sx={{
                                      fontSize: 20,
                                      color: theme.palette.text.secondary,
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <CloudDone
                                    sx={{
                                      fontSize: 20,
                                      color: theme.palette.success.main,
                                    }}
                                  />
                                }
                                sx={{
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(76, 175, 80, 0.08)",
                                  },
                                }}
                              />
                            </Tooltip>
                          </TableCell>
                        )}

                        <TableCell>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {formatPurchaseInvoiceNumberDisplay(invoice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {getEmitterName(invoice)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              EMITTER_TYPE_LABELS[getEmitterType(invoice)] ||
                              "Proveedor"
                            }
                            size="small"
                            color={
                              emitterTypeColors[getEmitterType(invoice)] ||
                              "info"
                            }
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontSize: { xs: "0.75rem", sm: "0.85rem" } }}
                          >
                            {invoice.issueDate
                              ? new Date(invoice.issueDate).toLocaleDateString(
                                  "es-ES",
                                  {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                  },
                                )
                              : "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabels[invoice.status]}
                            size="small"
                            color={statusColors[invoice.status]}
                            sx={{ fontWeight: 500, fontSize: "0.75rem" }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontWeight={600}
                            sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" } }}
                          >
                            {invoice.total?.toLocaleString("es-ES", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                            €
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="flex-end"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onView && (
                              <Tooltip title="Ver detalle">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onView(invoice);
                                  }}
                                  sx={{ color: theme.palette.info.main }}
                                >
                                  <Visibility
                                    sx={{ fontSize: { xs: 18, sm: 20 } }}
                                  />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Tooltip
                              title={
                                invoice.status ===
                                PURCHASE_INVOICE_STATUS.PAID
                                  ? "Marcar como NO PAGADA"
                                  : "Marcar como PAGADA"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) =>
                                  handleTogglePayment(invoice, e)
                                }
                                sx={{
                                  color:
                                    invoice.status ===
                                    PURCHASE_INVOICE_STATUS.PAID
                                      ? theme.palette.success.main
                                      : theme.palette.warning.main,
                                }}
                              >
                                <Payment
                                  sx={{ fontSize: { xs: 18, sm: 20 } }}
                                />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onEdit(invoice);
                                }}
                                sx={{ color: theme.palette.primary.main }}
                              >
                                <Edit sx={{ fontSize: { xs: 18, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteTarget(invoice);
                                }}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <Delete
                                  sx={{ fontSize: { xs: 18, sm: 20 } }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!compact && (
            <TablePagination
              component="div"
              count={totalInvoices}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[7, 10, 25, 50]}
              labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
              sx={{
                borderTop: "1px solid",
                borderColor: theme.palette.divider,
                "& .MuiTablePagination-toolbar": {
                  minHeight: 48,
                  px: { xs: 1, sm: 2 },
                  fontSize: { xs: "0.75rem", sm: "0.85rem" },
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  { fontSize: { xs: "0.7rem", sm: "0.85rem" } },
              }}
            />
          )}
        </Card>
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteInvoice}
        title="Eliminar Factura de Compra"
        message={`¿Eliminar factura ${deleteTarget?.invoiceNumber}?`}
        type="delete"
      />
    </Box>
  );
};

const getQuarterLabel = (quarter, year) => {
  const quarterLabels = {
    1: "1er Trimestre",
    2: "2do Trimestre",
    3: "3er Trimestre",
    4: "4to Trimestre",
  };
  return `${quarterLabels[quarter]} ${year}`;
};

export default PurchaseInvoiceList;